import { useState, useEffect, useCallback } from 'react';
import { doc, updateDoc, arrayUnion, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { toast } from 'react-toastify';
import logger from '../utils/logger';

/**
 * Custom Hook for Managing Photos
 *
 * Simplifies photo management for vehicles and job cards
 *
 * Usage:
 * const { photos, loading, addPhotos, removePhoto, refreshPhotos } = usePhotos('vehicle', vehicleId);
 */

export const usePhotos = (entityType, entityId) => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const collectionName = entityType === 'vehicle' ? 'vehicles' : 'jobs';

  // Fetch photos from Firestore
  const fetchPhotos = useCallback(async () => {
    if (!entityId) return;

    setLoading(true);
    setError(null);

    try {
      const docRef = doc(db, collectionName, entityId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        setPhotos(data.photos || []);
      } else {
        setPhotos([]);
      }
    } catch (err) {
      logger.error('Error fetching photos:', err);
      setError(err.message);
      toast.error('Failed to load photos');
    } finally {
      setLoading(false);
    }
  }, [entityId, collectionName]);

  // Initial fetch
  useEffect(() => {
    fetchPhotos();
  }, [fetchPhotos]);

  // Add new photos
  const addPhotos = async (newPhotos) => {
    if (!entityId || !newPhotos || newPhotos.length === 0) return;

    try {
      const docRef = doc(db, collectionName, entityId);

      await updateDoc(docRef, {
        photos: arrayUnion(...newPhotos),
        lastPhotoUpload: new Date().toISOString()
      });

      // Update local state
      setPhotos(prev => [...prev, ...newPhotos]);

      return { success: true };
    } catch (err) {
      logger.error('Error adding photos:', err);
      toast.error('Failed to save photo metadata');
      return { success: false, error: err.message };
    }
  };

  // Remove a photo
  const removePhoto = async (photoToRemove) => {
    if (!entityId || !photoToRemove) return;

    try {
      const docRef = doc(db, collectionName, entityId);

      // Get current photos
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) {
        throw new Error('Document not found');
      }

      const currentPhotos = docSnap.data().photos || [];

      // Filter out the removed photo
      const updatedPhotos = currentPhotos.filter(
        p => p.storagePath !== photoToRemove.storagePath
      );

      // Update Firestore
      await updateDoc(docRef, {
        photos: updatedPhotos
      });

      // Update local state
      setPhotos(updatedPhotos);

      return { success: true };
    } catch (err) {
      logger.error('Error removing photo:', err);
      toast.error('Failed to remove photo metadata');
      return { success: false, error: err.message };
    }
  };

  // Refresh photos from Firestore
  const refreshPhotos = () => {
    fetchPhotos();
  };

  return {
    photos,
    loading,
    error,
    addPhotos,
    removePhoto,
    refreshPhotos
  };
};
